/**
 * "Gert á eldri útgáfu nótnanna" UI pieces: the notice (with "Skoða og staðfesta" for the owner of a
 * stale layer and "Fela lög af eldri útgáfu" for everyone), the floating banner over the sheet, the
 * badge next to a layer's name and the panel's hide switch.
 */
import { BadgeCheck, EyeOff, TriangleAlert, X } from 'lucide-react';
import type { SongFileDTO } from '../../shared/dto/library';
import type { SheetAnnotations } from '../annotations/contract';
import { cx } from '../components/ui';
import { useT } from '../i18n';
import { useStaleActions } from './staleActions';
import { useStaleLayers } from './useStaleLayers';

const bannerButton =
  'inline-flex min-h-11 items-center gap-2 rounded-xl bg-white/80 px-3.5 text-[15px] font-semibold text-amber-950 ring-1 ring-amber-900/15 transition hover:bg-white dark:bg-amber-950/50 dark:text-amber-50 dark:ring-amber-100/20 dark:hover:bg-amber-950/70';

/** The "older version" notice with optional actions (owner: review & confirm; everyone: hide). */
export function StaleNotice({ onClose, onReview, onHide }: { onClose?: () => void; onReview?: () => void; onHide?: () => void }) {
  const t = useT();
  return (
    <div role="status" className="flex items-start gap-3 rounded-2xl bg-amber-100 px-4 py-3 text-amber-950 dark:bg-amber-900/40 dark:text-amber-100">
      <TriangleAlert className="mt-0.5 size-5 shrink-0" aria-hidden />
      <div className="min-w-0 flex-1">
        <p className="font-semibold">{t('annotate.stale')}</p>
        <p className="text-[15px]">{t('annotate.stale_hint')}</p>
        {onReview || onHide ? (
          <div className="mt-2 flex flex-wrap gap-2">
            {onReview ? (
              <button type="button" onClick={() => onReview()} className={bannerButton}>
                <BadgeCheck className="size-5 shrink-0" aria-hidden />
                {t('annotate.stale_review')}
              </button>
            ) : null}
            {onHide ? (
              <button type="button" onClick={() => onHide()} className={bannerButton}>
                <EyeOff className="size-5 shrink-0" aria-hidden />
                {t('annotate.stale_hide_all')}
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
      {onClose ? (
        <button type="button" onClick={onClose} aria-label={t('common.close')} className="-m-1 flex size-11 shrink-0 items-center justify-center rounded-xl hover:bg-black/5">
          <X className="size-5" aria-hidden />
        </button>
      ) : null}
    </div>
  );
}

/** Self-contained banner for one sheet: works in view and edit mode (own review sheet, engine filter). */
function SheetStaleBanner({ engine, file, onClose }: { engine: SheetAnnotations; file: SongFileDTO; onClose?: () => void }) {
  const c = useStaleLayers(engine, file);
  return (
    <>
      <StaleNotice onClose={onClose} onReview={c.canConfirm && c.own.length > 0 ? c.review : undefined} onHide={c.canHide ? c.hideAll : undefined} />
      {c.sheet}
    </>
  );
}

/** Without `ann`: the actions of the mounted layer panel (edit mode only; see staleActions.ts). */
function PanelStaleBanner({ onClose }: { onClose?: () => void }) {
  const actions = useStaleActions();
  return <StaleNotice onClose={onClose} onReview={actions && actions.ownCount > 0 ? actions.review : undefined} onHide={actions?.hideAll} />;
}

/**
 * Floating "older version" banner over the sheet (show it while engine.hasStaleLayers). Pass `ann`
 * (the useAnnotate result) and it works in BOTH view and edit mode: "Skoða og staðfesta" opens the
 * review right there (no edit mode needed) and "Fela lög af eldri útgáfu" sets the sheet's filter.
 * Without `ann` it borrows the actions of the mounted LayerPanel (edit mode only).
 */
export function StaleBanner({ ann, onClose }: { ann?: { engine: SheetAnnotations; file: SongFileDTO }; onClose?: () => void }) {
  return ann ? <SheetStaleBanner engine={ann.engine} file={ann.file} onClose={onClose} /> : <PanelStaleBanner onClose={onClose} />;
}

/** "Gert á eldri útgáfu" next to a layer's name. */
export function StaleBadge() {
  const t = useT();
  return (
    <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[13px] font-semibold whitespace-nowrap text-amber-950 dark:bg-amber-900/50 dark:text-amber-100">
      <TriangleAlert className="size-3.5" aria-hidden />
      {t('annotate.layer_stale_badge')}
    </span>
  );
}

/** "Fela lög af eldri útgáfu": the whole row is the switch (≥ 44 pt target, not just the track). */
export function StaleSwitch({ checked, count, onChange }: { checked: boolean; count: number; onChange: (hide: boolean) => void }) {
  const t = useT();
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className="flex min-h-14 w-full items-center gap-4 rounded-2xl bg-[var(--surface-2)] px-4 py-2 text-left transition hover:bg-[var(--accent-soft)]"
    >
      <span className="min-w-0 flex-1">
        <span className="block font-medium">{t('annotate.stale_hide_all')}</span>
        <span className="block text-[15px] text-[var(--muted)]">{t('annotate.stale_hide_hint', { count })}</span>
      </span>
      <span aria-hidden className={cx('relative h-8 w-14 shrink-0 rounded-full transition', checked ? 'bg-[var(--accent)]' : 'bg-[var(--line)]')}>
        <span className={cx('absolute top-1 size-6 rounded-full bg-white shadow transition-all', checked ? 'left-7' : 'left-1')} />
      </span>
    </button>
  );
}
